import React from 'react';

/**
 * EditorJS Renderer Component
 * Renders EditorJS content as HTML for display purposes
 * @param {string} content - EditorJS JSON content as string
 * @param {string} language - Current language
 * @param {string} defaultText - Fallback text if no content
 * @param {boolean} isHeader - Whether this is used in a header context (affects styling)
 */
const EditorJSRenderer = ({ content, language = 'it', defaultText = '', isHeader = false }) => {
  
  // Parse EditorJS content
  const parseContent = () => {
    if (!content) return null;

    try {
      const parsed = JSON.parse(content);
      if (parsed.blocks && Array.isArray(parsed.blocks)) {
        return parsed.blocks;
      }
    } catch (e) {
      // If not JSON, treat as plain text
      if (content.trim()) {
        return [{
          type: 'paragraph',
          data: { text: content }
        }];
      }
    }

    return null;
  };

  // Render a single block
  const renderBlock = (block, index) => {
    const { type, data } = block;

    switch (type) {
      case 'header':
        const level = data.level || 3;
        const HeaderTag = `h${level}`;
        return React.createElement(HeaderTag, {
          key: index,
          className: 'mb-2',
          dangerouslySetInnerHTML: { __html: data.text || '' }
        });

      case 'paragraph':
        if (!data.text || data.text.trim() === '') return null;
        return (
          <div
            key={index} 
            className="mb-1"
            dangerouslySetInnerHTML={{ __html: data.text }}
            style={{ lineHeight: '1.5' }}
          />
        );

      case 'list':
        const items = data.items || [];
        if (items.length === 0) return null;
        
        const listStyle = data.style === 'ordered' ? 'ol' : 'ul';
        const ListTag = listStyle;
        
        return React.createElement(ListTag, {
          key: index,
          className: 'mb-2'
        }, items.map((item, itemIndex) => (
          <li 
            key={itemIndex}
            dangerouslySetInnerHTML={{ __html: item }}
          />
        )));

      default:
        // For any other block type, try to render as paragraph
        if (data.text) {
          return (
            <div
              key={index}
              className="mb-1"
              dangerouslySetInnerHTML={{ __html: data.text }}
              style={{ lineHeight: '1.5' }}
            />
          );
        }
        return null;
    }
  };

  const blocks = parseContent();

  if (!blocks || blocks.length === 0) {
    return defaultText ? <span className="text-muted">{defaultText}</span> : null;
  }

  return (
    <div className={`editorjs-content ${isHeader ? 'header-content' : ''}`}>
      {blocks.map((block, index) => renderBlock(block, index))}
      
      <style>{`
        .editorjs-content h1, .editorjs-content h2, .editorjs-content h3, 
        .editorjs-content h4, .editorjs-content h5, .editorjs-content h6 {
          font-weight: 600;
          line-height: 1.2;
          margin-bottom: 0.25rem;
        }
        
        .editorjs-content h2 {
          font-size: 1.5rem;
        }
        
        .editorjs-content h3 {
          font-size: 1.25rem;
        }
        
        .editorjs-content h4 {
          font-size: 1.1rem;
        }
        
        .editorjs-content p, .editorjs-content div {
          line-height: 1.5;
          margin-bottom: 0.25rem;
        }
        
        .editorjs-content p:last-child, .editorjs-content div:last-child {
          margin-bottom: 0;
        }
        
        .editorjs-content ul, .editorjs-content ol {
          padding-left: 1.5rem;
          margin-bottom: 0.25rem;
        }
        
        .editorjs-content li {
          margin-bottom: 0.1rem;
        }
        
        .editorjs-content mark {
          background-color: #fff3cd;
          padding: 0.05rem 0.15rem;
          border-radius: 0.15rem;
        }
        
        .editorjs-content code {
          background-color: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 0.2rem;
          padding: 0.05rem 0.2rem;
          font-family: 'Courier New', monospace;
          font-size: 0.85rem;
          color: #e83e8c;
        }
        
        .editorjs-content u {
          text-decoration: underline;
        }
        
        .editorjs-content strong {
          font-weight: 600;
        }
        
        .editorjs-content em {
          font-style: italic;
        }
        
        .editorjs-content br {
          line-height: 1.5;
        }
        
        /* Stili per migliorare l'integrazione con Bootstrap */
        .editorjs-content {
          font-family: inherit;
          font-size: inherit;
          line-height: inherit;
          color: inherit;
          text-align: left;
        }
        
        .editorjs-content * {
          text-align: left;
        }
        
        /* Stili per header con testo bianco */
        .editorjs-content.header-content {
          color: inherit !important;
        }
        
        .editorjs-content.header-content * {
          color: inherit !important;
        }
        
        .editorjs-content.header-content code {
          background-color: rgba(255, 255, 255, 0.2);
          color: inherit;
          border-color: rgba(255, 255, 255, 0.3);
        }
        
        .editorjs-content.header-content mark {
          background-color: rgba(255, 255, 255, 0.3);
          color: inherit;
        }
      `}</style>
    </div>
  );
};

export default EditorJSRenderer; 